import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import { finalize } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { DynamicApiService } from './dynamic-api.service';
import {
  AddFieldRequest,
  DYNAMIC_FIELD_SEMANTIC_TYPE_CATALOG,
  DYNAMIC_FIELD_TYPE_CATALOG,
  DynamicEntity,
  DynamicEntityDetail,
  DynamicField,
  DynamicFieldOption,
  DynamicFieldSemanticType,
  DynamicFieldType,
  UpdateFieldRequest
} from './dynamic.models';

interface FieldFormState {
  name: string;
  displayName: string;
  description: string;
  fieldType: DynamicFieldType;
  maxLength?: number | null;
  isRequired: boolean;
  isUnique: boolean;
  isIndexed: boolean;
  defaultValue: string;
  defaultValueMode: 'none' | 'literal' | 'now';
  options: DynamicFieldOption[];
  lookupTargetEntityId: string | null;
  numericMin?: number | null;
  numericMax?: number | null;
  dateMin: string | null;
  dateMax: string | null;
  semanticType: DynamicFieldSemanticType | null;
  unit: string;
}

type SelectOption<T = string> = { label: string; value: T };
const NOW_DEFAULT_TOKEN = '__now__';

@Component({
  standalone: true,
  selector: 'ain-dynamic-field-editor',
  imports: [
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    InputNumberModule,
    SelectModule,
    CheckboxModule,
    TextareaModule,
    ConfirmDialogModule,
    DragDropModule,
    FormsModule
  ],
  providers: [MessageService, ConfirmationService],
  template: `
    <section class="field-editor">
      <div class="section-header">
        <h3>{{ t('dynamic.field.title') }}</h3>
        <ain-info-hint
          [titleKey]="'dynamic.field.info.title'"
          [descriptionKey]="'dynamic.field.info.description'"
          [bulletKeys]="[
            'dynamic.field.info.bullets.types',
            'dynamic.field.info.bullets.constraints',
            'dynamic.field.info.bullets.lookup'
          ]" />
      </div>

      @if (entity().isActive) {
        <div class="notice card">
          <strong>{{ t('dynamic.field.activeEntityNotice.title') }}</strong>
          <p>{{ t('dynamic.field.activeEntityNotice.description') }}</p>
        </div>
      }

      @if (!sortedFields().length) {
        <ain-state type="empty" [title]="t('common.noRecords')" />
        @if (canCreateField()) {
          <div class="empty-actions">
            <button pButton type="button" [label]="t('dynamic.field.add')" (click)="openAddDialog()"></button>
          </div>
        }
      } @else {
        <div class="toolbar">
          @if (canCreateField()) {
            <button pButton type="button" [label]="t('dynamic.field.add')" (click)="openAddDialog()"></button>
          }
        </div>

        <p-table
          cdkDropList
          cdkDropListLockAxis="y"
          (cdkDropListDropped)="onFieldDrop($event)"
          [value]="sortedFields()"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              @if (canReorderFields()) {
                <th style="width: 2.5rem"></th>
              }
              <th>{{ t('dynamic.field.displayName') }}</th>
              <th>{{ t('dynamic.field.name') }}</th>
              <th>{{ t('dynamic.field.type') }}</th>
              <th>{{ t('dynamic.field.isRequired') }}</th>
              <th>{{ t('dynamic.field.isUnique') }}</th>
              <th>{{ t('dynamic.field.isIndexed') }}</th>
              <th>{{ t('dynamic.field.semanticType') }}</th>
              <th style="width: 8rem"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-field>
            <tr
              cdkDrag
              [cdkDragData]="field"
              cdkDragLockAxis="y"
              [cdkDragDisabled]="!canReorderFields()">
              @if (canReorderFields()) {
                <td class="drag-handle" cdkDragHandle><i class="pi pi-bars"></i></td>
              }
              <td>{{ field.displayName }}</td>
              <td>{{ field.name }}</td>
              <td>{{ fieldTypeLabel(field.fieldType) }}</td>
              <td>{{ field.isRequired ? t('common.yes') : t('common.no') }}</td>
              <td>{{ field.isUnique ? t('common.yes') : t('common.no') }}</td>
              <td>{{ field.isIndexed ? t('common.yes') : t('common.no') }}</td>
              <td>{{ semanticTypeLabel(field.semanticType) }}</td>
              <td class="row-actions">
                @if (canUpdateField()) {
                  <button pButton type="button" text [label]="t('dynamic.field.edit')" (click)="openEditDialog(field)"></button>
                }
                @if (canDeleteField()) {
                  <button pButton type="button" text severity="danger" [label]="t('dynamic.field.delete')" (click)="confirmDelete(field)"></button>
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      }

      <p-dialog
        [header]="dialogMode() === 'add' ? t('dynamic.field.add') : t('dynamic.field.edit')"
        [(visible)]="fieldDialogVisible"
        [modal]="true"
        [style]="{ width: 'min(720px, 94vw)' }"
        (onHide)="resetFieldForm()">
        <div class="dialog-form">
          <ain-form-field [label]="t('dynamic.field.type')">
            <p-select
              [options]="fieldTypeOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="fieldForm.fieldType"
              (ngModelChange)="onFieldTypeChange($event)"
              appendTo="body" />
          </ain-form-field>

          @if (dialogMode() === 'add') {
            <ain-form-field [label]="t('dynamic.field.name')" [hint]="t('dynamic.entities.namePatternHint')">
              <input pInputText [(ngModel)]="fieldForm.name" />
            </ain-form-field>
          } @else {
            <ain-form-field [label]="t('dynamic.field.name')">
              <input pInputText [ngModel]="fieldForm.name" readonly />
            </ain-form-field>
          }

          <ain-form-field [label]="t('dynamic.field.displayName')">
            <input pInputText [(ngModel)]="fieldForm.displayName" />
          </ain-form-field>

          <ain-form-field [label]="t('dynamic.field.description')">
            <textarea pTextarea rows="2" [(ngModel)]="fieldForm.description"></textarea>
          </ain-form-field>

          <div class="checkbox-row">
            <label><p-checkbox binary="true" [(ngModel)]="fieldForm.isRequired" /><span>{{ t('dynamic.field.isRequired') }}</span></label>
            <label><p-checkbox binary="true" [(ngModel)]="fieldForm.isUnique" /><span>{{ t('dynamic.field.isUnique') }}</span></label>
            <label><p-checkbox binary="true" [(ngModel)]="fieldForm.isIndexed" /><span>{{ t('dynamic.field.isIndexed') }}</span></label>
          </div>

          @if (showMaxLength()) {
            <ain-form-field [label]="t('dynamic.field.maxLength')">
              <p-inputNumber [(ngModel)]="fieldForm.maxLength" [min]="1" [useGrouping]="false" />
            </ain-form-field>
          }

          @if (showDefaultValue()) {
            <ain-form-field [label]="t('dynamic.field.defaultValue')">
              @if (fieldForm.fieldType === 'Boolean') {
                <p-select
                  [options]="booleanDefaultOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="fieldForm.defaultValue"
                  [showClear]="true"
                  appendTo="body" />
              } @else if (fieldForm.fieldType === 'Date' || fieldForm.fieldType === 'DateTime') {
                <div class="date-default-editor">
                  <p-select
                    [options]="dateDefaultOptions()"
                    optionLabel="label"
                    optionValue="value"
                    [(ngModel)]="fieldForm.defaultValueMode"
                    appendTo="body" />
                  @if (fieldForm.defaultValueMode === 'literal') {
                    <ain-date-input [(ngModel)]="fieldForm.defaultValue" />
                  }
                </div>
              } @else if (fieldForm.fieldType === 'Select') {
                <p-select
                  [options]="optionDefaultChoices()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="fieldForm.defaultValue"
                  [showClear]="true"
                  appendTo="body" />
              } @else {
                <input pInputText [(ngModel)]="fieldForm.defaultValue" />
              }
            </ain-form-field>
          }

          @if (showOptions()) {
            <section class="options-editor card">
              <div class="section-header">
                <span>{{ t('dynamic.field.options') }}</span>
                <button pButton type="button" text [label]="t('dynamic.field.addOption')" (click)="addOptionRow()"></button>
              </div>
              @for (option of fieldForm.options; track $index; let idx = $index) {
                <div class="option-row">
                  <ain-form-field [label]="t('dynamic.field.optionValue')">
                    <input pInputText [(ngModel)]="option.value" [name]="'opt-value-' + idx" />
                  </ain-form-field>
                  <ain-form-field [label]="t('dynamic.field.optionLabel')">
                    <input pInputText [(ngModel)]="option.label" [name]="'opt-label-' + idx" />
                  </ain-form-field>
                  <button
                    pButton
                    type="button"
                    severity="danger"
                    text
                    icon="pi pi-trash"
                    [attr.aria-label]="t('dynamic.field.removeOption')"
                    (click)="removeOptionRow(idx)"></button>
                </div>
              }
            </section>
          }

          @if (showLookup()) {
            <ain-form-field [label]="t('dynamic.field.lookupTarget')">
              <p-select
                [options]="lookupEntityOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="fieldForm.lookupTargetEntityId"
                [filter]="true"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
          }

          @if (showNumericRange()) {
            <div class="range-row">
              <ain-form-field [label]="t('dynamic.field.numericMin')">
                <p-inputNumber [(ngModel)]="fieldForm.numericMin" [useGrouping]="false" />
              </ain-form-field>
              <ain-form-field [label]="t('dynamic.field.numericMax')">
                <p-inputNumber [(ngModel)]="fieldForm.numericMax" [useGrouping]="false" />
              </ain-form-field>
            </div>
          }

          @if (showDateRange()) {
            <div class="range-row">
              <ain-form-field [label]="t('dynamic.field.dateMin')">
                <ain-date-input [(ngModel)]="fieldForm.dateMin" />
              </ain-form-field>
              <ain-form-field [label]="t('dynamic.field.dateMax')">
                <ain-date-input [(ngModel)]="fieldForm.dateMax" />
              </ain-form-field>
            </div>
          }

          <div class="range-row">
            <ain-form-field [label]="t('dynamic.field.semanticType')">
              <p-select
                [options]="semanticTypeOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="fieldForm.semanticType"
                [showClear]="true"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('dynamic.field.unit')">
              <input pInputText [(ngModel)]="fieldForm.unit" [placeholder]="'%'" />
            </ain-form-field>
          </div>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="fieldDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="savingField()" (click)="saveField()"></button>
        </ng-template>
      </p-dialog>

      <p-confirmDialog />
    </section>
  `,
  styles: [`
    .field-editor { display: grid; gap: 12px; }
    .section-header { display: flex; align-items: center; gap: 8px; }
    .section-header h3 { margin: 0; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .notice.card { border: 1px solid #fcd34d; background: #fffbeb; border-radius: 10px; padding: 12px; }
    .notice.card p { margin: 6px 0 0; color: #92400e; }
    .drag-handle { cursor: grab; color: #6b7280; text-align: center; }
    .row-actions { display: flex; gap: 4px; flex-wrap: wrap; }
    .dialog-form { display: grid; gap: 12px; }
    .date-default-editor { display: grid; gap: 8px; }
    .checkbox-row { display: flex; gap: 16px; flex-wrap: wrap; }
    .checkbox-row label { display: inline-flex; align-items: center; gap: 8px; }
    .options-editor { display: grid; gap: 10px; border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; }
    .option-row { display: grid; gap: 8px; grid-template-columns: 1fr 1fr auto; align-items: end; }
    .range-row { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); }
    .card { border: 1px solid #e5e7eb; border-radius: 10px; }
    .empty-actions { display: flex; justify-content: center; margin-top: -8px; }
  `]
})
export class DynamicFieldEditorComponent implements OnInit {
  readonly entity = input.required<DynamicEntityDetail>();
  readonly refresh = output<void>();

  private readonly api = inject(DynamicApiService);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  private readonly confirmation = inject(ConfirmationService);
  private readonly contextService = inject(CurrentContextService);

  readonly savingField = signal(false);
  readonly dialogMode = signal<'add' | 'edit'>('add');
  readonly editingFieldId = signal<string | null>(null);
  readonly lookupEntities = signal<DynamicEntity[]>([]);

  fieldDialogVisible = false;
  fieldForm: FieldFormState = this.emptyFieldForm();

  readonly sortedFields = computed(() =>
    [...(this.entity().fields ?? [])].sort((left, right) => left.displayOrder - right.displayOrder)
  );

  readonly fieldTypeOptions = computed<SelectOption<DynamicFieldType>[]>(() =>
    DYNAMIC_FIELD_TYPE_CATALOG.map(item => ({
      value: item.value,
      label: this.t(item.labelKey)
    }))
  );

  readonly semanticTypeOptions = computed<SelectOption<DynamicFieldSemanticType | null>[]>(() => [
    { value: null, label: this.t('dynamic.field.semanticTypeAuto') },
    ...DYNAMIC_FIELD_SEMANTIC_TYPE_CATALOG.map(item => ({
      value: item.value,
      label: this.t(item.labelKey)
    }))
  ]);

  readonly lookupEntityOptions = computed<SelectOption[]>(() =>
    this.lookupEntities()
      .filter(item => item.id !== this.entity().id)
      .map(item => ({
        value: item.id,
        label: `${item.displayName} (${item.name})`
      }))
  );

  showMaxLength(): boolean {
    return ['Text', 'LongText'].includes(this.fieldForm.fieldType);
  }

  showOptions(): boolean {
    return ['Select', 'MultiSelect'].includes(this.fieldForm.fieldType);
  }

  showLookup(): boolean {
    return this.fieldForm.fieldType === 'Lookup';
  }

  showNumericRange(): boolean {
    return ['Integer', 'Decimal', 'Money'].includes(this.fieldForm.fieldType);
  }

  showDateRange(): boolean {
    return ['Date', 'DateTime'].includes(this.fieldForm.fieldType);
  }

  showDefaultValue(): boolean {
    return !['Json', 'FileReference', 'Lookup', 'MultiSelect'].includes(this.fieldForm.fieldType);
  }

  ngOnInit(): void {
    this.api.listEntities().subscribe({
      next: items => this.lookupEntities.set(items ?? []),
      error: () => this.lookupEntities.set([])
    });
  }

  fieldTypeLabel(fieldType: string): string {
    const key = `dynamic.fieldType.${fieldType}`;
    const translated = this.t(key);
    return translated === key ? fieldType : translated;
  }

  semanticTypeLabel(semanticType?: string | null): string {
    if (!semanticType) {
      return this.t('dynamic.field.semanticTypeAuto');
    }
    const key = `dynamic.semanticType.${semanticType}`;
    const translated = this.t(key);
    return translated === key ? semanticType : translated;
  }

  booleanDefaultOptions(): SelectOption[] {
    return [
      { label: this.t('common.yes'), value: 'true' },
      { label: this.t('common.no'), value: 'false' }
    ];
  }

  optionDefaultChoices(): SelectOption[] {
    return this.fieldForm.options
      .filter(option => option.value.trim().length > 0)
      .map(option => ({
        value: option.value,
        label: option.label.trim() || option.value
      }));
  }

  dateDefaultOptions(): SelectOption<'none' | 'now' | 'literal'>[] {
    return [
      { label: this.t('common.none'), value: 'none' },
      { label: this.t('dynamic.default.now'), value: 'now' },
      { label: this.t('dynamic.default.customDate'), value: 'literal' }
    ];
  }

  onFieldTypeChange(fieldType: DynamicFieldType): void {
    this.fieldForm.fieldType = fieldType;
    if (fieldType !== 'Date' && fieldType !== 'DateTime') {
      this.fieldForm.defaultValueMode = 'literal';
      return;
    }

    const trimmedDefault = this.fieldForm.defaultValue.trim();
    if (!trimmedDefault) {
      this.fieldForm.defaultValueMode = 'none';
    } else if (trimmedDefault.toLowerCase() === NOW_DEFAULT_TOKEN) {
      this.fieldForm.defaultValueMode = 'now';
    } else {
      this.fieldForm.defaultValueMode = 'literal';
    }
  }

  openAddDialog(): void {
    this.dialogMode.set('add');
    this.editingFieldId.set(null);
    this.fieldForm = this.emptyFieldForm();
    this.fieldDialogVisible = true;
  }

  openEditDialog(field: DynamicField): void {
    this.dialogMode.set('edit');
    this.editingFieldId.set(field.id);
    this.fieldForm = {
      name: field.name,
      displayName: field.displayName,
      description: field.description ?? '',
      fieldType: field.fieldType as DynamicFieldType,
      maxLength: field.fieldType === 'Text' || field.fieldType === 'LongText' ? this.parseMaxLength(field.sqlDataType) : null,
      isRequired: field.isRequired,
      isUnique: field.isUnique,
      isIndexed: field.isIndexed,
      defaultValue: field.defaultValue ?? '',
      defaultValueMode: this.resolveDateDefaultMode(field.fieldType as DynamicFieldType, field.defaultValue ?? ''),
      options: (field.options ?? []).map(option => ({ ...option })),
      lookupTargetEntityId: field.lookupTargetEntityId ?? null,
      numericMin: field.numericMin ?? null,
      numericMax: field.numericMax ?? null,
      dateMin: field.dateMin ?? null,
      dateMax: field.dateMax ?? null,
      semanticType: (field.semanticType as DynamicFieldSemanticType | null) ?? null,
      unit: field.unit ?? ''
    };
    this.fieldDialogVisible = true;
  }

  addOptionRow(): void {
    this.fieldForm.options = [...this.fieldForm.options, { value: '', label: '' }];
  }

  removeOptionRow(index: number): void {
    this.fieldForm.options = this.fieldForm.options.filter((_, rowIndex) => rowIndex !== index);
  }

  saveField(): void {
    const displayName = this.fieldForm.displayName.trim();
    if (!displayName) {
      this.messages.add({ severity: 'warn', summary: this.t('dynamic.field.displayName') });
      return;
    }

    if (this.dialogMode() === 'add') {
      const name = this.fieldForm.name.trim();
      if (!name) {
        this.messages.add({ severity: 'warn', summary: this.t('dynamic.field.name') });
        return;
      }
      this.createField(name, displayName);
      return;
    }

    const fieldId = this.editingFieldId();
    if (!fieldId) {
      return;
    }
    this.updateField(fieldId, displayName);
  }

  confirmDelete(field: DynamicField): void {
    this.confirmation.confirm({
      header: this.t('dynamic.confirm.title'),
      message: this.t('dynamic.field.confirm.delete'),
      accept: () => this.deleteField(field.id)
    });
  }

  onFieldDrop(event: CdkDragDrop<DynamicField[]>): void {
    if (!this.canReorderFields() || event.previousIndex === event.currentIndex) {
      return;
    }

    const reordered = [...this.sortedFields()];
    moveItemInArray(reordered, event.previousIndex, event.currentIndex);
    const fieldIds = reordered.map(field => field.id);

    this.api.reorderFields(this.entity().id, { fieldIds }).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.field.reordered') });
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  canCreateField(): boolean {
    return this.hasFieldAction('Create');
  }

  canUpdateField(): boolean {
    return this.hasFieldAction('Update');
  }

  canDeleteField(): boolean {
    return this.hasFieldAction('Delete');
  }

  canReorderFields(): boolean {
    return this.hasFieldAction('Reorder');
  }

  private createField(name: string, displayName: string): void {
    const request = this.buildAddRequest(name, displayName);
    this.savingField.set(true);
    this.api.addField(this.entity().id, request).pipe(
      finalize(() => this.savingField.set(false))
    ).subscribe({
      next: () => {
        this.fieldDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('dynamic.field.saved') });
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private updateField(fieldId: string, displayName: string): void {
    const request = this.buildUpdateRequest(displayName);
    this.savingField.set(true);
    this.api.updateField(this.entity().id, fieldId, request).pipe(
      finalize(() => this.savingField.set(false))
    ).subscribe({
      next: () => {
        this.fieldDialogVisible = false;
        this.messages.add({ severity: 'success', summary: this.t('dynamic.field.saved') });
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private deleteField(fieldId: string): void {
    this.api.deleteField(this.entity().id, fieldId).subscribe({
      next: () => {
        this.messages.add({ severity: 'success', summary: this.t('dynamic.field.deleted') });
        this.refresh.emit();
      },
      error: () => this.messages.add({ severity: 'error', summary: this.t('common.saveFailed') })
    });
  }

  private buildAddRequest(name: string, displayName: string): AddFieldRequest {
    const request: AddFieldRequest = {
      name,
      displayName,
      fieldType: this.fieldForm.fieldType,
      isRequired: this.fieldForm.isRequired,
      isUnique: this.fieldForm.isUnique,
      isIndexed: this.fieldForm.isIndexed,
      displayOrder: this.sortedFields().length,
      description: this.fieldForm.description.trim() || undefined
    };

    this.applyConditionalPayload(request);
    return request;
  }

  private buildUpdateRequest(displayName: string): UpdateFieldRequest {
    const request: UpdateFieldRequest = {
      displayName,
      isRequired: this.fieldForm.isRequired,
      displayOrder: this.sortedFields().find(field => field.id === this.editingFieldId())?.displayOrder ?? 0,
      description: this.fieldForm.description.trim() || undefined,
      fieldType: this.fieldForm.fieldType,
      isUnique: this.fieldForm.isUnique,
      isIndexed: this.fieldForm.isIndexed
    };

    this.applyConditionalPayload(request);
    return request;
  }

  private applyConditionalPayload(request: AddFieldRequest | UpdateFieldRequest): void {
    const defaultValue = this.resolveRequestDefaultValue();
    if (defaultValue) {
      request.defaultValue = defaultValue;
    }

    if (this.showMaxLength() && this.fieldForm.maxLength != null) {
      request.maxLength = this.fieldForm.maxLength;
    }

    if (this.showOptions()) {
      request.options = this.fieldForm.options
        .map(option => ({ value: option.value.trim(), label: option.label.trim() || option.value.trim() }))
        .filter(option => option.value.length > 0);
    }

    if (this.showLookup() && this.fieldForm.lookupTargetEntityId) {
      request.lookupTargetEntityId = this.fieldForm.lookupTargetEntityId;
    }

    if (this.showNumericRange()) {
      if (this.fieldForm.numericMin != null) request.numericMin = this.fieldForm.numericMin;
      if (this.fieldForm.numericMax != null) request.numericMax = this.fieldForm.numericMax;
    }

    if (this.showDateRange()) {
      if (this.fieldForm.dateMin) request.dateMin = this.fieldForm.dateMin;
      if (this.fieldForm.dateMax) request.dateMax = this.fieldForm.dateMax;
    }

    if (this.fieldForm.semanticType) {
      request.semanticType = this.fieldForm.semanticType;
    }

    const unit = this.fieldForm.unit.trim();
    if (unit) {
      request.unit = unit;
    }
  }

  private parseMaxLength(sqlDataType: string): number | null {
    const match = /nvarchar\((\d+)\)/i.exec(sqlDataType);
    return match ? Number(match[1]) : null;
  }

  private emptyFieldForm(): FieldFormState {
    return {
      name: '',
      displayName: '',
      description: '',
      fieldType: 'Text',
      maxLength: null,
      isRequired: false,
      isUnique: false,
      isIndexed: false,
      defaultValue: '',
      defaultValueMode: 'none',
      options: [],
      lookupTargetEntityId: null,
      numericMin: null,
      numericMax: null,
      dateMin: null,
      dateMax: null,
      semanticType: null,
      unit: ''
    };
  }

  private resolveRequestDefaultValue(): string {
    if (this.fieldForm.fieldType === 'Date' || this.fieldForm.fieldType === 'DateTime') {
      return this.fieldForm.defaultValueMode === 'now'
        ? NOW_DEFAULT_TOKEN
        : this.fieldForm.defaultValueMode === 'literal'
          ? this.fieldForm.defaultValue.trim()
          : '';
    }

    return this.fieldForm.defaultValue.trim();
  }

  private resolveDateDefaultMode(fieldType: DynamicFieldType, defaultValue: string): FieldFormState['defaultValueMode'] {
    if (fieldType !== 'Date' && fieldType !== 'DateTime') {
      return 'literal';
    }

    const normalized = defaultValue.trim().toLowerCase();
    if (!normalized) {
      return 'none';
    }

    return normalized === NOW_DEFAULT_TOKEN ? 'now' : 'literal';
  }

  resetFieldForm(): void {
    this.fieldForm = this.emptyFieldForm();
    this.editingFieldId.set(null);
  }

  private hasFieldAction(action: string): boolean {
    const context = this.contextService.context();
    const roles = new Set(context?.roles ?? []);
    if (roles.has('SystemAdmin')) {
      return true;
    }

    const node = (context?.permissions ?? []).find(item => item.key === 'dynamic.fields');
    if (!node) {
      return false;
    }

    const actions = new Set((node.actions ?? []).map(item => item.toLowerCase()));
    return actions.has(action.toLowerCase());
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
